import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Trophy, ExternalLink, X } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const WonProofsSection = () => {
  const [proofs, setProofs] = useState([]);
  const [viewing, setViewing] = useState(null); // proof being previewed

  useEffect(() => {
    axios.get(`${API}/won-proofs`).then(r => setProofs(r.data || [])).catch(() => setProofs([]));
  }, []);

  if (!proofs.length) return null;

  const count = proofs.length;
  const gridCols =
    count === 1 ? "grid-cols-1 max-w-sm mx-auto" :
    count === 2 ? "sm:grid-cols-2 max-w-2xl mx-auto" :
    count === 3 ? "sm:grid-cols-2 lg:grid-cols-3" :
    "sm:grid-cols-2 lg:grid-cols-4";

  return (
    <section className="py-10 px-4 bg-gradient-to-b from-emerald-50/40 to-white border-t border-emerald-100" data-testid="won-proofs-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <Badge className="bg-emerald-600 text-white text-xs px-3 py-1 mb-2 inline-flex items-center gap-1.5" data-testid="won-proofs-badge">
            <Trophy className="h-3 w-3" /> VERIFIED WINS
          </Badge>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Recent Winning Proof</h2>
          <p className="text-sm text-gray-500 mt-1">Real wins, verified tickets. Click image to enlarge.</p>
        </div>

        <div className={`grid gap-4 ${gridCols}`}>
          {proofs.map((p, i) => (
            <Card key={p.proof_id || i} className="overflow-hidden border border-gray-200 hover:border-emerald-400 hover:shadow-lg transition-all bg-white group">
              <button
                type="button"
                onClick={() => setViewing(p)}
                className="relative aspect-[4/3] bg-gray-50 overflow-hidden w-full block cursor-zoom-in"
                aria-label={`View larger image of ${p.title || "won proof"}`}
                data-testid={`won-proof-image-${i}`}
              >
                <img
                  src={p.image_url}
                  alt={p.title || `Won proof ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <Badge className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] px-2 py-0.5 shadow pointer-events-none">
                  <Trophy className="h-3 w-3 mr-1 inline" /> WON
                </Badge>
              </button>
              {(p.title || p.description || p.link) && (
                <div className="p-3" data-testid={`won-proof-meta-${i}`}>
                  {p.title && <p className="font-bold text-gray-900 text-sm leading-tight" data-testid={`won-proof-title-${i}`}>{p.title}</p>}
                  {p.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
                  {p.link && (
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-700 font-bold mt-2 inline-flex items-center gap-1 hover:underline"
                      data-testid={`won-proof-link-${i}`}
                      onClick={e => e.stopPropagation()}
                    >
                      Verify ticket <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* Lightbox dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="won-proof-lightbox">
          <DialogTitle className="sr-only">{viewing?.title || "Won proof image"}</DialogTitle>
          <button
            type="button"
            onClick={() => setViewing(null)}
            className="absolute top-3 right-3 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-1.5 transition"
            aria-label="Close"
            data-testid="won-proof-lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
          {viewing && (
            <div className="flex flex-col">
              <img
                src={viewing.image_url}
                alt={viewing.title || "Won proof"}
                className="w-full max-h-[75vh] object-contain bg-black"
                data-testid="won-proof-lightbox-img"
              />
              {(viewing.title || viewing.description || viewing.link) && (
                <div className="p-4 bg-gray-900 text-white space-y-2">
                  {viewing.title && <p className="font-bold text-lg">{viewing.title}</p>}
                  {viewing.description && <p className="text-sm text-gray-300">{viewing.description}</p>}
                  {viewing.link && (
                    <a
                      href={viewing.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-400 font-bold inline-flex items-center gap-1 hover:underline"
                      data-testid="won-proof-lightbox-link"
                    >
                      Verify ticket <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default WonProofsSection;
